"use client";

import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import type { KyanIcon, WalletIconVariant } from "@kyan/ui";
import { Button, cn, Icon, Select, SelectItem, WalletIcon } from "@kyan/ui";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { routes } from "#/config/routes";
import { useWalletButtonFeatureFlag } from "#/hooks/featureFlags/useWalletButtonFeatureFlag";
import { useHandleLogIn } from "#/hooks/useHandleLogIn";
import { useHandleLogOut } from "#/hooks/useHandleLogOut";
import { useTheme } from "#/hooks/useTheme";
import { useMirrorAccount } from "#/store/app/devMode/mirrorAccount";
import { useEoa } from "#/store/wallet/eoa/eoa";
import { getChainName } from "#/utils/chain";
import { copy } from "#/utils/copy";
import { formatAddress } from "#/utils/formatAddress";

export interface WalletDropDownCreateItems {
  address?: string;
  themeSwitchIcon: KyanIcon;
  onThemeClick: () => void;
  logout: () => void;
  redirect: (route: string) => void;
}

const createItems = ({
  address,
  themeSwitchIcon,
  logout,
  redirect,
  onThemeClick,
}: WalletDropDownCreateItems): {
  icon: KyanIcon;
  children: React.ReactNode;
  onClick?: () => void;
}[] => [
  {
    icon: "copy",
    children: (
      <div className="grid place-items-start gap-0.5">
        <span className="text-textPrimary text-xs">Copy address</span>
        <span className="text-xs">{formatAddress(address ?? "")}</span>
      </div>
    ),
    onClick: () => {
      copy(address ?? "");
    },
  },
  {
    icon: "settings",
    children: <span className="text-textPrimary">Settings</span>,
    onClick: () => {
      redirect(routes.portfolio.settings);
    },
  },
  {
    icon: themeSwitchIcon,
    children: <span className="text-textPrimary">Theme</span>,
    onClick: () => {
      onThemeClick();
    },
  },
  {
    icon: "exit",
    children: <span className="text-textPrimary">Disconnect</span>,
    onClick: () => {
      logout();
    },
  },
];

export const WalletDropdown = () => {
  const { handleLogIn } = useHandleLogIn();
  const { handleLogOut } = useHandleLogOut();
  const { primaryWallet, network } = useDynamicContext();
  const { eoa, isEoaConnected } = useEoa();
  const router = useRouter();
  const { isDark, handleToggleTheme } = useTheme();
  const [key, setKey] = useState(0);
  const { mirrorAccount } = useMirrorAccount();
  const walletButtonFlag = useWalletButtonFeatureFlag();

  const themeSwitchIcon = isDark ? "sun" : "moon";

  const isDisabledByFeatureFlag = !walletButtonFlag?.isEnabled;

  if (!isEoaConnected) {
    return (
      <div className="border-divider flex h-full items-center justify-center pr-4">
        <Button
          size="lg"
          className="w-38.25 shadow-none"
          disabled={isDisabledByFeatureFlag}
          onClick={handleLogIn}
        >
          {isDisabledByFeatureFlag
            ? (walletButtonFlag?.title ?? "Connect Wallet")
            : "Connect Wallet"}
        </Button>
      </div>
    );
  }

  const items = createItems({
    address: eoa,
    themeSwitchIcon,
    logout: handleLogOut,
    onThemeClick: handleToggleTheme,
    redirect: (route: string) => {
      router.push(route);
    },
  });

  return (
    <Select
      key={key}
      value={undefined}
      variant="toolbar"
      contentClassName="rounded-br-none dark:left-px"
      triggerClassName="relative group gap-2 w-auto xl:w-44.5 h-full border-r-0 aria-expanded:bg-white dark:aria-expanded:bg-buttonHover"
      valueComponent={
        <div className="flex items-center gap-3">
          {primaryWallet && (
            <WalletIcon
              size="sm"
              variant={
                primaryWallet.connector.name.toLowerCase() as WalletIconVariant
              }
              className={cn(
                "ml-1 grayscale transition duration-500",
                "group-hover:grayscale-0 group-aria-expanded:grayscale-0",
              )}
            />
          )}

          <div className="grid place-items-center gap-0.5">
            <span className="hidden text-xs xl:inline">Connected wallet</span>
            <span className="text-textPrimary" data-testid="account-address">
              <span className="hidden xl:inline">{formatAddress(eoa)}</span>
              <span className="inline xl:hidden">{formatAddress(eoa, 0)}</span>
            </span>
            {network && (
              <span className="text-[9px]">{getChainName(network)}</span>
            )}
            {mirrorAccount && (
              <span className="text-primary y-0 text-xs">Mirrored Account</span>
            )}
          </div>
        </div>
      }
      onValueChange={(value) => {
        items.find(({ icon }) => icon === value)?.onClick?.();
        setKey((prev) => prev + 1);
      }}
    >
      {items.map((item) => (
        <SelectItem
          key={item.icon}
          value={item.icon}
          className="h-auto p-3 text-xs"
          itemComponent={
            <div className="flex w-full cursor-pointer items-center justify-between">
              {item.children}
              <Icon size="sm" className="mr-1" name={item.icon} />
            </div>
          }
        />
      ))}
    </Select>
  );
};
